/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from "react-router-dom";
// @mui material components
import { ThemeProvider } from "@mui/material/styles";



// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";


// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator, UserDataContext } from "context";

// Images
import useUserRoutes from "userRoutes";
import { Box, CircularProgress } from "@mui/material";
import axios from "axios";
import { GoogleOAuthProvider } from '@react-oauth/google';
import SideNavBar from "TailwindcssComponents/SideNavBar";
import RoleSelectionModel from "pages/Models/roleSelectionModel";
import VerifyEmail from "pages/verifyEmail";
import VerifyToken from "pages/verifyToken";
import AddUserRoles from "pages/addUserRoles";

export default function App() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const clientId = process.env.REACT_APP_API_GOOGLE_CLIENT_ID;
  const bearerToken = localStorage.getItem('token');
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const [selectedOrganization, setSelectedOrganization] = useState(() => {
    const saved = localStorage.getItem('selectedOrganization');
    return saved ? JSON.parse(saved) : null;
  });
  const [organizations, setOrganizations] = useState([]);
  const [requireOrgSelection, setRequireOrgSelection] = useState(false);
  const { userRoutes, isLoading } = useUserRoutes(selectedOrganization);
  const [userData, setUserData] = useState();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tokenParam = params.get('token');
  const roleNameParam = params.get('name');
  const orgNameParam = params.get('orgName');
  const navigate = useNavigate();

  const fetchUser = async () => {
    try {
      if (bearerToken) {
        const userResponse = await axios.get(`${apiUrl}auth-service/auth/my`, {
          headers: { Authorization: `Bearer ${bearerToken}` }
        });
        if (userResponse.data.success) {
          const user = userResponse.data.data;
          setUserData(user);
          const organizations = userResponse.data.data.userRoles;
          setOrganizations(organizations);

          if (organizations.length === 1 && !selectedOrganization) {
            setRequireOrgSelection(false);
            setSelectedOrganization(organizations[0]);
            localStorage.setItem('selectedOrganization', JSON.stringify(organizations[0]));
          } else if (organizations.length > 1 && !selectedOrganization) {
            setRequireOrgSelection(true); // Only if not selected yet
          } else if (organizations.length === 0) {
            setRequireOrgSelection(false);
            setSelectedOrganization(null);
            localStorage.removeItem('selectedOrganization');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [bearerToken]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };


  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes && allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    });

  useEffect(() => {
    getRoutes()
  }, [userRoutes])

  useEffect(() => {
    if (tokenParam && roleNameParam && orgNameParam && userData?.emailVerified) {
      navigate(`/userRoles/add-userRoles?token=${tokenParam}&roleName=${roleNameParam}&orgName=${orgNameParam}`);
    }
  }, [userData]);

  const contextValue = useMemo(() => ({ userData, fetchUser, selectedOrganization }), [userData, selectedOrganization]);

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <GoogleOAuthProvider clientId={clientId}>
        <UserDataContext.Provider value={contextValue}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
              <CircularProgress color="info" />
            </Box>
          ) : requireOrgSelection && !selectedOrganization ? (
            <RoleSelectionModel organizations={organizations} onSelect={(org) => {
              setSelectedOrganization(org);
            }} />
          ) : (
            <>
              {bearerToken ? (
                <>
                  {/* {userRoutes.length >= 1 ? ( */}
                  <>
                    {layout === "dashboard" && <SideNavBar routes={userRoutes} />}
                    <Routes>
                      {getRoutes(userRoutes)}
                      <Route path="*" element={<Navigate to="/dashboard" />} />
                      <Route path="/verifyEmail" element={<VerifyEmail />} />
                      <Route path="/verifyToken" element={<VerifyToken />} />
                      <Route path="/userRoles/add-userRoles" element={<AddUserRoles />} />
                    </Routes>
                  </>
                  {/* ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                      <CircularProgress color="info" />
                    </Box>
                  )} */}
                </>
              ) : (
                <Routes>
                  {getRoutes(userRoutes)}
                  <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
                </Routes>
              )}
            </>
          )}
        </UserDataContext.Provider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
}